import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import DisclaimerFooter from '../components/DisclaimerFooter';
import { townSchoolMoaData as moa } from '../data/townSchoolMoaData';
import './TownSchoolMoa.css';
import { usePersistentFlag } from '../utils/expandedState';

// A deliberately simple plan page: the ten goals of the Town–School agreement
// and who is responsible for each. Each goal is a sentence or two, so they are a
// plain numbered list rather than accordions; the "Whereas" preamble is tucked
// into one collapsible section so the goals lead.
function TownSchoolMoa() {
  const [preambleExpanded, setPreambleExpanded] = usePersistentFlag('town-school-moa:preambleExpanded');

  useEffect(() => {
    document.title = 'Town–School Memorandum of Agreement';
  }, []);

  return (
    <div className="moa-page">
      <a href="#main-content" className="moa-skip-link">Skip to main content</a>
      <Header
        title={moa.shortTitle}
        creditExtra="website design by Michael Rubenstein"
      />

      <main id="main-content" tabIndex={-1} className="moa-content">
        <div className="moa-intro">
          <h2 className="moa-formal-title">{moa.formalTitle}</h2>
          <p className="moa-adopted">Adopted {moa.adoptedDate}</p>
        </div>

        <div className="moa-preamble">
          <button
            className={`moa-preamble-header ${preambleExpanded ? 'expanded' : ''}`}
            onClick={() => setPreambleExpanded(!preambleExpanded)}
            aria-expanded={preambleExpanded}
          >
            <span className={`moa-chevron${preambleExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
            <span className="moa-preamble-title">Preamble</span>
          </button>
          {preambleExpanded && (
            <div className="moa-preamble-body">
              {moa.preamble.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          )}
        </div>

        <section className="moa-goals" aria-labelledby="moa-goals-heading">
          <h2 id="moa-goals-heading" className="moa-goals-heading">Goals</h2>
          <p className="moa-lead-in">{moa.leadIn}</p>

          <ol className="moa-goal-list">
            {moa.goals.map((goal) => (
              <li key={goal.number} className="moa-goal" value={goal.number}>
                <p className="moa-goal-text">{goal.text}</p>
                <p className="moa-responsible">
                  <span className="moa-responsible-label">Responsible:</span>{' '}
                  {goal.responsible.map((party, i) => (
                    <React.Fragment key={party}>
                      {/* A spoken comma between parties; the badges alone would
                          be read as one run-together word. */}
                      {i > 0 && <span className="visually-hidden">, </span>}
                      <span className={`moa-badge moa-badge-${party.toLowerCase()}`}>{party}</span>
                    </React.Fragment>
                  ))}
                </p>
              </li>
            ))}
          </ol>
        </section>

        <div className="moa-source">
          <p>
            Source: {moa.formalTitle}, dated {moa.adoptedDate}. Goal text is transcribed from the agreement.
            Responsible parties are shown as <strong>Schools</strong> (the Public Schools of Brookline, PSB) and{' '}
            <strong>Town</strong> (the Town of Brookline, TOB).
          </p>
        </div>

        <DisclaimerFooter>
          <p>
            For source information from the Town of Brookline, go to the{' '}
            <a
              href="https://www.brooklinema.gov/DocumentCenter/View/63392/PSB-TOB-MOU-2026"
              target="_blank"
              rel="noopener noreferrer"
            >
              Memorandum of Agreement<span className="visually-hidden"> (PDF, opens in new tab)</span>
            </a>
            {' '}on the Town of Brookline website.
          </p>
        </DisclaimerFooter>
      </main>
    </div>
  );
}

export default TownSchoolMoa;
